import React, { useCallback, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Fingerprint, Loader2, Trash2, X } from "lucide-react";
import { apiUrl } from "../api";
import { decodeCreationOptions, isWebAuthnSupported, serializeCredential } from "../utils/webauthn";

const TEXT = {
  en: {
    title: "Passkeys",
    subtitle: "Use a passkey as an additional MFA method after username and password.",
    loading: "Loading passkeys...",
    unsupported: "This browser does not support passkeys.",
    noPasskeys: "No passkeys are registered for this Super Admin account.",
    label: "Passkey label",
    labelPlaceholder: "Example: Windows Hello on this laptop",
    codeLabel: "Current authenticator code",
    add: "Add passkey",
    adding: "Adding...",
    delete: "Delete",
    deleting: "Deleting...",
    deleteCode: "Authenticator code to delete",
    addFailed: "Could not register passkey.",
    deleteFailed: "Could not delete passkey.",
    loadFailed: "Could not load passkeys.",
    added: "Passkey registered.",
    deleted: "Passkey deleted.",
    created: "Created",
    lastUsed: "Last used",
    backup: "Synced/backup",
    yes: "Yes",
    no: "No",
    never: "Never",
  },
  he: {
    title: "מפתחות גישה",
    subtitle: "שימוש במפתח גישה כאמצעי MFA נוסף לאחר שם משתמש וסיסמה.",
    loading: "טוען מפתחות גישה...",
    unsupported: "הדפדפן הזה אינו תומך במפתחות גישה.",
    noPasskeys: "לא רשומים מפתחות גישה לחשבון Super Admin זה.",
    label: "שם מפתח הגישה",
    labelPlaceholder: "לדוגמה: Windows Hello במחשב הזה",
    codeLabel: "קוד אימות נוכחי",
    add: "הוסף מפתח גישה",
    adding: "מוסיף...",
    delete: "מחק",
    deleting: "מוחק...",
    deleteCode: "קוד אימות למחיקה",
    addFailed: "לא ניתן לרשום מפתח גישה.",
    deleteFailed: "לא ניתן למחוק מפתח גישה.",
    loadFailed: "לא ניתן לטעון מפתחות גישה.",
    added: "מפתח הגישה נרשם.",
    deleted: "מפתח הגישה נמחק.",
    created: "נוצר",
    lastUsed: "שימוש אחרון",
    backup: "מסונכרן/מגובה",
    yes: "כן",
    no: "לא",
    never: "מעולם לא",
  },
};

const normalizeLanguage = (language) => (language === "en" ? "en" : "he");

const passkeyLocale = (language) => (language === "en" ? "en-US" : "he-IL");

const hasExplicitTimezone = (value) => /(?:z|[+-]\d{2}:\d{2})$/i.test(value.trim());

const normalizePasskeyTimestamp = (value) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";
  const isoLike = trimmed.replace(" ", "T");
  return hasExplicitTimezone(trimmed) ? isoLike : `${isoLike}Z`;
};

const formatPasskeyTimestamp = (value, language) => {
  const trimmed = String(value || "").trim();
  if (!trimmed) return "";

  const date = new Date(normalizePasskeyTimestamp(trimmed));
  if (Number.isNaN(date.getTime())) {
    return trimmed;
  }

  try {
    const parts = new Intl.DateTimeFormat(passkeyLocale(language), {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    }).formatToParts(date);
    const part = (type) => parts.find((entry) => entry.type === type)?.value;
    return `${part("year")}-${part("month")}-${part("day")} ${part("hour")}:${part("minute")}:${part("second")}`;
  } catch (error) {
    return trimmed;
  }
};

const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("userToken") || ""}`,
});

const PasskeySecuritySection = ({ language = "he" }) => {
  const uiLanguage = normalizeLanguage(language);
  const t = TEXT[uiLanguage];
  const isRtl = uiLanguage === "he";
  const [passkeys, setPasskeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [working, setWorking] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [label, setLabel] = useState("");
  const [totpCode, setTotpCode] = useState("");
  const [deleteCodes, setDeleteCodes] = useState({});
  const [deletingId, setDeletingId] = useState(null);

  const loadPasskeys = useCallback(async () => {
    setLoading(true);
    setFeedback(null);
    try {
      const response = await fetch(apiUrl("/auth?action=passkey_status"), { headers: authHeaders() });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || t.loadFailed);
      setPasskeys(Array.isArray(data?.passkeys) ? data.passkeys : []);
    } catch (error) {
      setFeedback({ type: "error", text: error.message || t.loadFailed });
    } finally {
      setLoading(false);
    }
  }, [t.loadFailed]);

  useEffect(() => {
    loadPasskeys();
  }, [loadPasskeys]);

  const addPasskey = async (event) => {
    event.preventDefault();
    const cleanCode = totpCode.replace(/\D/g, "").slice(0, 6);
    if (!isWebAuthnSupported()) {
      setFeedback({ type: "error", text: t.unsupported });
      return;
    }
    if (cleanCode.length !== 6) {
      setFeedback({ type: "error", text: t.addFailed });
      return;
    }

    setWorking(true);
    setFeedback(null);
    try {
      const optionsResponse = await fetch(apiUrl("/auth?action=passkey_registration_options"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ code: cleanCode, label: label.trim() }),
      });
      const optionsData = await optionsResponse.json().catch(() => ({}));
      if (!optionsResponse.ok || !optionsData?.options || !optionsData?.challenge_id) {
        throw new Error(optionsData?.error || t.addFailed);
      }

      const credential = await navigator.credentials.create({
        publicKey: decodeCreationOptions(optionsData.options),
      });
      if (!credential) {
        throw new Error(t.addFailed);
      }

      const verifyResponse = await fetch(apiUrl("/auth?action=passkey_registration_verify"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          challenge_id: optionsData.challenge_id,
          label: label.trim(),
          credential: serializeCredential(credential),
        }),
      });
      const verifyData = await verifyResponse.json().catch(() => ({}));
      if (!verifyResponse.ok) {
        throw new Error(verifyData?.error || t.addFailed);
      }

      setPasskeys(Array.isArray(verifyData?.passkeys) ? verifyData.passkeys : []);
      setLabel("");
      setTotpCode("");
      setFeedback({ type: "success", text: t.added });
    } catch (error) {
      setFeedback({ type: "error", text: error.message || t.addFailed });
    } finally {
      setWorking(false);
    }
  };

  const deletePasskey = async (id) => {
    const cleanCode = String(deleteCodes[id] || "").replace(/\D/g, "").slice(0, 6);
    if (cleanCode.length !== 6) {
      setFeedback({ type: "error", text: t.deleteFailed });
      return;
    }

    setDeletingId(id);
    setFeedback(null);
    try {
      const response = await fetch(apiUrl("/auth?action=passkey_delete"), {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ id, code: cleanCode }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) throw new Error(data?.error || t.deleteFailed);
      setPasskeys(Array.isArray(data?.passkeys) ? data.passkeys : []);
      setDeleteCodes((current) => ({ ...current, [id]: "" }));
      setFeedback({ type: "success", text: t.deleted });
    } catch (error) {
      setFeedback({ type: "error", text: error.message || t.deleteFailed });
    } finally {
      setDeletingId(null);
    }
  };

  if (loading) {
    return <div className="text-sm font-black text-slate-500">{t.loading}</div>;
  }

  return (
    <div className="space-y-5" dir={isRtl ? "rtl" : "ltr"}>
      <div className="rounded-3xl border border-slate-100 bg-white p-5 shadow-sm">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-700">
              <Fingerprint size={24} />
            </div>
            <div>
              <h4 className="text-lg font-black text-slate-950">{t.title}</h4>
              <p className="mt-1 text-sm font-bold leading-6 text-slate-500">{t.subtitle}</p>
            </div>
          </div>
          {!isWebAuthnSupported() && (
            <div className="flex items-center gap-2 rounded-2xl bg-amber-50 px-4 py-3 text-sm font-black text-amber-800">
              <AlertTriangle size={17} />
              {t.unsupported}
            </div>
          )}
        </div>

        {feedback && <FeedbackMessage message={feedback} />}

        <form onSubmit={addPasskey} className="mt-5 grid gap-3 lg:grid-cols-[1fr_180px_auto]">
          <label className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.label}</span>
            <input
              value={label}
              onChange={(event) => setLabel(event.target.value)}
              placeholder={t.labelPlaceholder}
              autoComplete="off"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-black text-slate-900 outline-none focus:border-blue-400"
            />
          </label>
          <label className="block">
            <span className="mb-2 block text-xs font-black uppercase tracking-widest text-slate-500">{t.codeLabel}</span>
            <input
              value={totpCode}
              onChange={(event) => setTotpCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              inputMode="numeric"
              autoComplete="one-time-code"
              className="w-full rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-center text-sm font-black tracking-[0.2em] text-slate-900 outline-none focus:border-blue-400"
            />
          </label>
          <div className="flex items-end">
            <button
              type="submit"
              disabled={working || !isWebAuthnSupported()}
              className="inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-5 py-3 text-sm font-black text-white transition hover:bg-slate-800 disabled:opacity-60"
            >
              {working ? <Loader2 size={17} className="animate-spin" /> : <Fingerprint size={17} />}
              {working ? t.adding : t.add}
            </button>
          </div>
        </form>

        <div className="mt-5 space-y-3">
          {passkeys.length === 0 && (
            <div className="rounded-2xl bg-slate-50 px-4 py-4 text-sm font-bold text-slate-500">{t.noPasskeys}</div>
          )}
          {passkeys.map((passkey) => (
            <div key={passkey.id} className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <div className="font-black text-slate-950">{passkey.label || t.title}</div>
                  <div className="mt-1 text-xs font-bold leading-5 text-slate-500">
                    {t.created}: {formatPasskeyTimestamp(passkey.created_at, uiLanguage) || "-"} · {t.lastUsed}:{" "}
                    {formatPasskeyTimestamp(passkey.last_used_at, uiLanguage) || t.never} · {t.backup}:{" "}
                    {passkey.backup_state === true ? t.yes : passkey.backup_state === false ? t.no : "-"}
                  </div>
                </div>
                <div className="flex flex-wrap items-end gap-2">
                  <label className="block">
                    <span className="sr-only">{t.deleteCode}</span>
                    <input
                      value={deleteCodes[passkey.id] || ""}
                      onChange={(event) =>
                        setDeleteCodes((current) => ({
                          ...current,
                          [passkey.id]: event.target.value.replace(/\D/g, "").slice(0, 6),
                        }))
                      }
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      placeholder={t.deleteCode}
                      className="h-11 w-[190px] rounded-xl border border-slate-200 bg-white px-3 text-center text-xs font-black tracking-[0.18em] text-slate-900 outline-none focus:border-blue-400"
                    />
                  </label>
                  <button
                    type="button"
                    onClick={() => deletePasskey(passkey.id)}
                    disabled={deletingId === passkey.id}
                    className="inline-flex h-11 items-center gap-2 rounded-xl bg-rose-50 px-4 text-xs font-black text-rose-700 transition hover:bg-rose-100 disabled:opacity-60"
                  >
                    {deletingId === passkey.id ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                    {deletingId === passkey.id ? t.deleting : t.delete}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const FeedbackMessage = ({ message }) => {
  const isError = message?.type === "error";
  return (
    <div className={`mt-4 flex items-center gap-2 rounded-2xl px-4 py-3 text-sm font-black ${isError ? "bg-rose-50 text-rose-700" : "bg-emerald-50 text-emerald-700"}`}>
      {isError ? <X size={17} /> : <CheckCircle2 size={17} />}
      {message.text}
    </div>
  );
};

export default PasskeySecuritySection;
